import React, { useState } from 'react';
import { X, ArrowRight, CheckCircle, AlertCircle, Star } from 'lucide-react';
import { Lesson, LessonStep } from '../types';

interface LessonModalProps {
  lesson: Lesson;
  onClose: () => void;
  onComplete: (lesson: Lesson, earnedXp: number) => void;
}

const LessonModal: React.FC<LessonModalProps> = ({ lesson, onClose, onComplete }) => {
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState<string>('');
  const [showResult, setShowResult] = useState(false);
  const [earnedXp, setEarnedXp] = useState(0);
  const [quizScores, setQuizScores] = useState<number[]>([]);

  // Check if lesson steps exist and have content
  if (!lesson.steps || lesson.steps.length === 0) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-2xl w-full max-w-2xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold">{lesson.title}</h2>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="text-center py-8">
            <p className="text-gray-600">No steps available for this lesson.</p>
          </div>
        </div>
      </div>
    );
  }

  const currentStep = lesson.steps[currentStepIndex];
  const isLastStep = currentStepIndex === lesson.steps.length - 1;

  const calculateStars = () => {
    const quizSteps = lesson.steps.filter(step => step.type === 'quiz');
    if (quizSteps.length === 0) return 3; // If no quizzes, award full stars for completion
    
    const averageScore = quizScores.reduce((a, b) => a + b, 0) / quizScores.length;
    if (averageScore >= 90) return 3;
    if (averageScore >= 70) return 2;
    return 1;
  };

  const handleNext = () => {
    if (currentStep.type === 'quiz') {
      if (!showResult) {
        setShowResult(true);
        const isCorrect = selectedAnswer === currentStep.correctAnswer;
        const score = isCorrect ? 100 : 0;
        setQuizScores([...quizScores, score]);
        if (isCorrect) {
          setEarnedXp(prev => prev + currentStep.xpReward);
        }
        return;
      }
      setShowResult(false);
    } else {
      setEarnedXp(prev => prev + currentStep.xpReward);
    }

    if (isLastStep) {
      const stars = calculateStars();
      const updatedLesson = { ...lesson, stars };
      onComplete(updatedLesson, earnedXp);
    } else {
      setCurrentStepIndex(prev => prev + 1);
      setSelectedAnswer('');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-xl font-bold">{lesson.title}</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Progress bar */}
        <div className="w-full h-1 bg-gray-200">
          <div 
            className="h-full bg-blue-500 transition-all duration-300"
            style={{ width: `${((currentStepIndex + 1) / lesson.steps.length) * 100}%` }}
          />
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="max-w-xl mx-auto">
            <h3 className="text-lg font-semibold mb-4">{currentStep.title}</h3>
            
            {currentStep.type === 'content' ? (
              <div className="prose">{currentStep.content}</div>
            ) : (
              <div className="space-y-4">
                <p className="mb-4">{currentStep.content}</p>
                <div className="space-y-2">
                  {currentStep.quizOptions?.map((option) => (
                    <button
                      key={option}
                      onClick={() => !showResult && setSelectedAnswer(option)}
                      className={`
                        w-full p-4 rounded-lg border-2 text-left transition-all
                        ${showResult 
                          ? option === currentStep.correctAnswer
                            ? 'border-green-500 bg-green-50'
                            : option === selectedAnswer
                              ? 'border-red-500 bg-red-50'
                              : 'border-gray-200'
                          : selectedAnswer === option
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:border-blue-200'
                        }
                      `}
                    >
                      {option}
                    </button>
                  ))}
                </div>
                {showResult && (
                  <div className={`mt-4 p-4 rounded-lg ${selectedAnswer === currentStep.correctAnswer ? 'bg-green-100' : 'bg-red-100'}`}>
                    {selectedAnswer === currentStep.correctAnswer ? (
                      <div className="flex items-center text-green-700">
                        <CheckCircle className="w-5 h-5 mr-2" />
                        <span>Correct! +{currentStep.xpReward} XP</span>
                      </div>
                    ) : (
                      <div className="flex items-center text-red-700">
                        <AlertCircle className="w-5 h-5 mr-2" />
                        <span>Not quite. The correct answer was: {currentStep.correctAnswer}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t flex justify-between items-center">
          <div className="text-sm text-gray-500">
            Step {currentStepIndex + 1} of {lesson.steps.length}
          </div>
          <div className="flex items-center space-x-4">
            {isLastStep && (
              <div className="flex items-center">
                {[1, 2, 3].map((star) => (
                  <Star
                    key={star}
                    className={`w-6 h-6 ${
                      star <= calculateStars()
                        ? 'text-yellow-400 fill-current'
                        : 'text-gray-300'
                    }`}
                  />
                ))}
              </div>
            )}
            <button
              onClick={handleNext}
              disabled={currentStep.type === 'quiz' && !selectedAnswer && !showResult}
              className={`
                px-6 py-2 rounded-full flex items-center
                ${currentStep.type === 'quiz' && !selectedAnswer && !showResult
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-blue-500 text-white hover:bg-blue-600'
                }
              `}
            >
              {isLastStep ? 'Complete' : 'Next'}
              <ArrowRight className="w-4 h-4 ml-2" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LessonModal;